export default {
  domain: 'O',
  title: 'Openness To Experience',
  shortDescription: 'Openness to Experience describes a dimension of cognitive style that distinguishes imaginative, creative people from down-to-earth, conventional people.',
  description: `Open people are intellectually curious,
appreciative of art, and sensitive to beauty. They tend to be,
compared to closed people, more aware of their feelings. They tend to
think and act in individualistic and nonconforming
ways. Intellectuals typically score high on Openness to Experience;
consequently, this factor has also been called Culture or
Intellect. <br /><br />Nonetheless, Intellect is probably best regarded as one aspect of openness
to experience. Scores on Openness to Experience are only modestly
related to years of education and scores on standard intelligent tests.
<br /><br />
Another characteristic of the open cognitive style is a facility for thinking
in symbols and abstractions far removed from concrete experience. Depending on
the individual's specific intellectual abilities, this symbolic cognition may
take the form of mathematical, logical, or geometric thinking, artistic and
metaphorical use of language, music composition or performance, or one of the
many visual or performing arts.
<br /><br />
People with low scores on openness to experience tend to have narrow, common
interests. They prefer the plain, straightforward, and obvious over the
complex, ambiguous, and subtle. They may regard the arts and sciences with
suspicion, regarding these endeavors as abstruse or of no practical use.
Closed people prefer familiarity over novelty; they are conservative and
resistant to change.
<br /><br />
Openness is often presented as healthier or more mature by psychologists, who
are often themselves open to experience. However, open and closed styles of
thinking are useful in different environments. The intellectual style of the
open person may serve a professor well, but research has shown that closed
thinking is related to superior job performance in police work, sales, and
a number of service occupations.`,
  results: [
    {
      score: 'low', // do not translate this line
      text: `Your score on Openness to Experience is low, indicating you like to think in
plain and simple terms. Others describe you as down-to-earth, practical,
and conservative.`
    },
    {
      score: 'neutral', // do not translate this line
      text: `Your score on Openness to Experience is average, indicating you enjoy
tradition but are willing to try new things. Your thinking is neither
simple nor complex. To others you appear to be a well-educated person
but not an intellectual.`
    },
    {
      score: 'high', // do not translate this line
      text: `Your score on Openness to Experience is high, indicating you enjoy novelty,
variety, and change. You are curious, imaginative, and creative.`
    }
  ],
  facets: [
    {
      facet: 1,
      title: 'Imagination',
      text: `To imaginative individuals, the real world is
often too plain and ordinary. High scorers on this scale use fantasy as a
way of creating a richer, more interesting world. Low scorers are on this
scale are more oriented to facts than fantasy.`
    },
    {
      facet: 2,
      title: 'Artistic Interests',
      text: `High scorers on this scale love beauty, both in
art and in nature. They become easily involved and absorbed in artistic
and natural events. They are not necessarily artistically trained nor
talented, although many will be. The defining features of this scale are
interest in, and appreciation of natural and
artificial beauty. Low scorers lack aesthetic sensitivity and interest in
the arts.`
    },
    {
      facet: 3,
      title: 'Emotionality',
      text: `Persons high on Emotionality have good access
to and awareness of their own feelings. Low scorers are less aware of
their feelings and tend not to express their emotions openly.`
    },
    {
      facet: 4,
      title: 'Adventurousness',
      text: `High scorers on adventurousness are eager to
try new activities, travel to foreign lands, and experience different
things. They find familiarity and routine boring, and will take a new
route home just because it is different. Low scorers tend to feel
uncomfortable with change and prefer familiar routines.`
    },
    {
      facet: 5,
      title: 'Intellect',
      text: `Intellect and artistic interests are the two most
important, central aspects of openness to experience. High scorers on
Intellect love to play with ideas. They are open-minded to new and unusual
ideas, and like to debate intellectual issues. They enjoy riddles, puzzles,
and brain teasers. Low scorers on Intellect prefer dealing with either
people or things rather than ideas. They regard intellectual exercises as a
waste of time. Intellect should not be equated with intelligence.
Intellect is an intellectual style, not an intellectual ability, although
high scorers on Intellect score slightly higher than low-Intellect
individuals on standardized intelligence tests.`
    },
    {
      facet: 6,
      title: 'Liberalism',
      text: `Psychological liberalism refers to a readiness to
challenge authority, convention, and traditional values. In its most
extreme form, psychological liberalism can even represent outright
hostility toward rules, sympathy for law-breakers, and love of ambiguity,
chaos, and disorder. Psychological conservatives prefer the security and
stability brought by conformity to tradition. Psychological liberalism
and conservatism are not identical to political affiliation, but certainly
incline individuals toward certain political parties.`
    }
  ]
};
